import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { PlanReveal, PLAN_THEME } from "../components/PlanReveal";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import logo from "../assets/trimo-logo.png";

export default function PaymentStatus({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { lang, L } = useLang();
  const [status, setStatus] = useState(mode === "cancel" ? "cancel" : "checking");
  const [info, setInfo] = useState({ plan: "pro", billing: "monthly" });
  const [reveal, setReveal] = useState(false);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (mode === "cancel") return;
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo({ plan: data.plan || "pro", billing: data.billing || "monthly" });
          setStatus("success");
          checkAuth();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) {}
      if (attempts < 12) setTimeout(poll, 2000);
      else setStatus("error");
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId, mode]);

  const es = lang === "es";
  const content = {
    checking: {
      icon: <Loader2 className="h-14 w-14 text-yellow-400 animate-spin" />,
      title:L("Confirmando tu pago...", "Confirming your payment..."),
      sub:L("Un momento, por favor.", "One moment, please."),
    },
    success: {
      icon: <CheckCircle2 className="h-14 w-14 text-yellow-400" />,
      title:L("¡Pago completado!", "Payment complete!"),
      sub:L("Tu plan ya está activo. ¡A crear clips virales!", "Your plan is active. Let's create viral clips!"),
    },
    cancel: {
      icon: <XCircle className="h-14 w-14 text-zinc-500" />,
      title:L("Pago cancelado", "Payment cancelled"),
      sub:L("No se ha realizado ningún cargo.", "No charge was made."),
    },
    error: {
      icon: <XCircle className="h-14 w-14 text-red-400" />,
      title:L("Algo salió mal", "Something went wrong"),
      sub:L("No pudimos confirmar el pago. Intenta de nuevo.", "We couldn't confirm the payment. Please try again."),
    },
  }[status];

  if (status === "success") return (<>
    {!reveal && <SuccessView plan={info.plan} billing={info.billing} L={L} onDone={() => setReveal(true)} />}
    {reveal && <PlanReveal plan={info.plan} onDone={() => navigate("/studio")} />}
  </>);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground px-5">
      <img src={logo} alt="Vitled" className="h-10 w-10 rounded-xl mb-8" />
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-5">{content.icon}</div>
        <h1 className="text-2xl font-bold">{content.title}</h1>
        <p className="mt-2 text-zinc-400">{content.sub}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Button onClick={() => navigate("/studio")} className="bg-yellow-400 hover:bg-yellow-300 text-zinc-950 font-semibold rounded-xl">
            {L("Ir al Studio", "Go to Studio")}
          </Button>
          <Link to="/">
            <Button variant="outline" className="border-white/15 bg-transparent rounded-xl">
              {L("Inicio", "Home")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}


function SuccessView({ plan, billing, L, onDone }) {
  const t = PLAN_THEME[plan] || PLAN_THEME.pro;
  const Icon = t.icon;
  const perks = [
    L(`${t.exports} exportaciones al mes`, `${t.exports} exports per month`),
    L(`Exportación ${t.res}`, `${t.res} export`),
    L("Sin marca de agua", "No watermark"),
    L("Subtítulos virales y emojis con IA", "Viral captions & AI emojis"),
  ];
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070707] text-white grid place-items-center px-5 py-12" data-testid="payment-success">
      <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.07) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="absolute left-1/2 top-1/3 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px]" style={{ background: t.glow }} />
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl p-8 shadow-2xl">
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-3xl" style={{ background: `linear-gradient(90deg, ${t.c1}, ${t.c2})` }} />
        <div className="flex justify-center">
          <div className="relative grid h-24 w-24 place-items-center">
            <motion.span className="absolute inset-0 rounded-full" style={{ border: `2px solid ${t.c1}` }} initial={{ scale: 0.6, opacity: 1 }} animate={{ scale: 1.6, opacity: 0 }} transition={{ duration: 1.6, repeat: Infinity }} />
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.2 }}
              className="grid h-20 w-20 place-items-center rounded-full" style={{ background: `linear-gradient(135deg, ${t.c1}, ${t.c2})`, boxShadow: `0 10px 50px -5px ${t.glow}` }}>
              <Check className="h-10 w-10 text-black" strokeWidth={3} />
            </motion.div>
          </div>
        </div>
        <h1 className="mt-6 text-center text-3xl font-black tracking-tight" data-testid="payment-success-title">{L("¡Pago completado!", "Payment complete!")}</h1>
        <p className="mt-2 text-center text-sm text-zinc-400">{L("Gracias por confiar en Vitled. Tu plan ya está activo.", "Thanks for choosing Vitled. Your plan is now active.")}</p>
        <div className="mt-6 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="payment-success-plan">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `linear-gradient(135deg, ${t.c1}, ${t.c2})` }}><Icon className="h-5 w-5 text-black" /></span>
            <div><p className="text-xs text-zinc-500">{L("Tu plan", "Your plan")}</p><p className="text-lg font-extrabold">Vitled {t.name}</p></div>
          </div>
          <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${t.c1}22`, color: t.c1 }}>{billing === "yearly" ? L("Anual", "Yearly") : L("Mensual", "Monthly")}</span>
        </div>
        <ul className="mt-5 space-y-2.5">
          {perks.map((p, i) => (
            <motion.li key={p} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.1 }} className="flex items-center gap-2.5 text-sm text-zinc-200">
              <span className="grid h-5 w-5 place-items-center rounded-full" style={{ background: `${t.c1}22` }}><Check className="h-3 w-3" style={{ color: t.c1 }} strokeWidth={3} /></span>{p}
            </motion.li>
          ))}
        </ul>
        <button onClick={onDone} data-testid="payment-success-done"
          className="group mt-8 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-extrabold text-black transition-transform hover:scale-[1.02] active:scale-[0.99]"
          style={{ background: `linear-gradient(135deg, ${t.c1}, ${t.c2})`, boxShadow: `0 15px 40px -12px ${t.glow}` }}>
          <Sparkles className="h-5 w-5" />{L("Hecho", "Done")}
        </button>
        <p className="mt-4 text-center text-[11px] text-zinc-600">{L("Recibirás el recibo por email de Stripe.", "You'll receive the receipt by email from Stripe.")}</p>
      </motion.div>
    </div>
  );
}
